import api from "../api/api";
const BASE_URL = import.meta.env.VITE_APP_API_URL;

export const sapMovimentacaoEstoqueService = {
  /**
   * Listar todas as movimentações com filtros opcionais
   * @param {Object} filtros - Filtros para a busca
   * @param {string} filtros.tipo_movimentacao - Tipo: ENTRADA ou SAIDA
   * @param {string} filtros.status - Status da movimentação (PENDENTE, PROCESSADA, ERRO)
   * @param {string} filtros.deposito - Código do depósito
   * @param {string} filtros.item_codigo - Código do item
   * @param {string} filtros.data_inicio - Data de início (YYYY-MM-DD)
   * @param {string} filtros.data_fim - Data de fim (YYYY-MM-DD)
   * @param {string} filtros.arquivo_gerado - 'sim' ou 'nao'
   * @returns {Promise<Object>}
   */
  async listarMovimentacoes(filtros = {}) {
    try {
      const params = new URLSearchParams();

      if (filtros.tipo_movimentacao) params.append('tipo_movimentacao', filtros.tipo_movimentacao);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.deposito) params.append('deposito', filtros.deposito);
      if (filtros.item_codigo) params.append('item_codigo', filtros.item_codigo);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      if (filtros.arquivo_gerado) params.append('arquivo_gerado', filtros.arquivo_gerado);

      const url = `${BASE_URL}/sap-movimentacoes-estoque?${params.toString()}`;
      console.log("🔍 Listando movimentações:", url);

      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        const paginationData = response.data.data || response.data;
        const movimentacoes = paginationData.data || paginationData.dados || [];
        const total = paginationData.total || 0;

        console.log("✅ Movimentações recebidas:", movimentacoes.length);

        return {
          success: true,
          message: response.data.mensagem || "Movimentações listadas com sucesso",
          total: total,
          dados: movimentacoes,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao listar movimentações.");
      }
    } catch (error) {
      console.error("❌ Erro ao listar movimentações:", error);
      handleError(error, "Erro ao listar movimentações.");
      return {
        success: false,
        message: "Erro ao listar movimentações.",
        total: 0,
        dados: [],
      };
    }
  },

  /**
   * Buscar uma movimentação específica por ID
   * @param {number} id
   * @returns {Promise<Object>}
   */
  async buscarMovimentacao(id) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque/${id}`;
      console.log("🔍 Buscando movimentação:", url);

      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Movimentação encontrada",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar movimentação.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar movimentação:", error);
      handleError(error, "Erro ao buscar movimentação.");
      return {
        success: false,
        message: "Erro ao buscar movimentação.",
        dados: null,
      };
    }
  },

  /**
   * Criar uma nova movimentação de estoque
   * @param {Object} dados
   * @param {string} dados.tipo_movimentacao - ENTRADA ou SAIDA
   * @param {string} dados.item_codigo
   * @param {string} dados.item_descricao
   * @param {number} dados.quantidade
   * @param {string} dados.deposito
   * @param {string} dados.posicao
   * @param {number} dados.saldo_estoque
   * @param {string} dados.unidade_medida
   * @param {string} dados.unidade_medida_estoque
   * @param {string} dados.usuario
   * @returns {Promise<Object>}
   */
  async criarMovimentacao(dados) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque`;
      console.log("➕ Criando movimentação:", url, dados);

      const response = await api.post(url, dados);

      if (response.status === 201 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Movimentação criada com sucesso",
          dados: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao criar movimentação.");
      }
    } catch (error) {
      console.error("❌ Erro ao criar movimentação:", error);
      handleError(error, "Erro ao criar movimentação.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao criar movimentação.",
        erros: error.response?.data?.erros || null,
      };
    }
  },

  /**
   * Excluir uma movimentação (apenas se estiver PENDENTE)
   * @param {number} id
   * @returns {Promise<Object>}
   */
  async excluirMovimentacao(id) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque/${id}`;
      console.log("🗑️ Excluindo movimentação:", url);

      const response = await api.delete(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Movimentação excluída com sucesso",
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao excluir movimentação.");
      }
    } catch (error) {
      console.error("❌ Erro ao excluir movimentação:", error);
      handleError(error, "Erro ao excluir movimentação.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao excluir movimentação.",
      };
    }
  },

  /**
   * Obter estatísticas das movimentações
   * @param {Object} filtros
   * @param {string} filtros.tipo_movimentacao - ENTRADA ou SAIDA (opcional)
   * @returns {Promise<Object>}
   */
  async estatisticas(filtros = {}) {
    try {
      const params = new URLSearchParams();
      if (filtros.tipo_movimentacao) params.append('tipo_movimentacao', filtros.tipo_movimentacao);

      const url = `${BASE_URL}/sap-movimentacoes-estoque/estatisticas?${params.toString()}`;
      console.log("📊 Buscando estatísticas:", url);

      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Estatísticas carregadas",
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao buscar estatísticas.");
      }
    } catch (error) {
      console.error("❌ Erro ao buscar estatísticas:", error);
      return {
        success: false,
        message: "Erro ao buscar estatísticas.",
        data: null,
      };
    }
  },

  /**
   * Gerar arquivo Excel de movimentações (SEM download automático)
   * @param {Object} dados
   * @param {Array} dados.movimentacoes - Array de IDs das movimentações
   * @param {string} dados.nome_arquivo - Nome do arquivo (opcional)
   * @returns {Promise<Object>}
   */
  async gerarExcel(dados) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque/gerar-excel`;
      console.log("📊 Gerando Excel:", url, dados);

      const response = await api.post(url, dados);

      if (response.status === 201 && response.data) {
        console.log("✅ Excel gerado:", response.data);
        return {
          success: true,
          message: response.data.mensagem || "Excel gerado com sucesso! Acesse o Painel SAP Downloads.",
          data: response.data.data,
        };
      } else if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Excel gerado com sucesso!",
          data: response.data.data,
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao gerar Excel.");
      }
    } catch (error) {
      console.error("❌ Erro ao gerar Excel:", error);
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao gerar Excel.",
      };
    }
  },

  /**
   * Baixar arquivo Excel existente
   * @param {string} nomeArquivo
   * @returns {Promise<Object>}
   */
  async baixarExcel(nomeArquivo) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque/baixar-excel/${nomeArquivo}`;
      console.log("📥 Baixando Excel:", url);

      const response = await api.get(url, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(downloadUrl);

        return {
          success: true,
          message: "Excel baixado com sucesso",
          nomeArquivo: nomeArquivo,
        };
      } else {
        throw new Error("Erro ao baixar Excel.");
      }
    } catch (error) {
      console.error("❌ Erro ao baixar Excel:", error);
      handleError(error, "Erro ao baixar Excel.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao baixar Excel.",
      };
    }
  },

  /**
   * Listar arquivos Excel gerados
   * @param {Object} filtros
   * @param {string} filtros.tipo_movimentacao - ENTRADA ou SAIDA (opcional)
   * @returns {Promise<Object>}
   */
  async listarArquivosExcel(filtros = {}) {
    try {
      const params = new URLSearchParams();
      if (filtros.tipo_movimentacao) params.append('tipo_movimentacao', filtros.tipo_movimentacao);

      const url = `${BASE_URL}/sap-movimentacoes-estoque/arquivos-excel?${params.toString()}`;
      console.log("📋 Listando arquivos Excel:", url);

      const response = await api.get(url);

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivos listados com sucesso",
          dados: response.data.data || [],
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao listar arquivos.");
      }
    } catch (error) {
      console.error("❌ Erro ao listar arquivos Excel:", error);
      handleError(error, "Erro ao listar arquivos Excel.");
      return {
        success: false,
        message: "Erro ao listar arquivos Excel.",
        dados: [],
      };
    }
  },

  /**
   * Cancelar arquivo Excel gerado
   * @param {string} arquivoExportado - Nome do arquivo a cancelar
   * @returns {Promise<Object>}
   */
  async cancelarArquivoExcel(arquivoExportado) {
    try {
      const url = `${BASE_URL}/sap-movimentacoes-estoque/cancelar-arquivo-excel`;
      console.log("🗑️ Cancelando arquivo Excel:", url, arquivoExportado);

      const response = await api.post(url, { arquivo_exportado: arquivoExportado });

      if (response.status === 200 && response.data) {
        return {
          success: true,
          message: response.data.mensagem || "Arquivo cancelado com sucesso",
          dados: response.data.data || {},
        };
      } else {
        throw new Error(response.data?.mensagem || "Erro ao cancelar arquivo.");
      }
    } catch (error) {
      console.error("❌ Erro ao cancelar arquivo Excel:", error);
      handleError(error, "Erro ao cancelar arquivo Excel.");
      return {
        success: false,
        message: error.response?.data?.mensagem || "Erro ao cancelar arquivo Excel.",
      };
    }
  },
};

/**
 * Tratamento genérico de erros
 */
function handleError(error, defaultMessage) {
  if (error.response) {
    console.error("Resposta do servidor:", error.response.status, error.response.data);
  } else if (error.request) {
    console.error("Sem resposta do servidor:", error.request);
  } else {
    console.error("Erro na requisição:", error.message);
  }
}
